import { Manrope, Nunito_Sans, Roboto, Roboto_Condensed, Syncopate } from 'next/font/google';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
    type CSSProperties,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import GlobalThemeSetter from "../components/context/GlobalThemeSetter";
import { AuthProvider } from '../components/context/Info';
// MULTI-FILIAIS, modo 'tela': o visitante escolhe a filial antes de ver o site.
// O portão é minúsculo e devolve null fora do modo tela; a tela em si (JS + CSS)
// só é carregada para quem usa.
import FilialTelaPortao from '../components/fixed/filiais/FilialTelaPortao';
import { PreviaBuilderProvider } from '../lib/previaBuilder';
import { avenirBook } from '../components/fonts/avenir'; // <- o arquivo acima
import { allDataHomePage } from '../services/home';
import { salvaLogs } from '../services/properties';
import '../styles/globals.css';
import '../utils/polyfills';
import { isColorLight } from '../utils/FunctionsGlobals';
import { clearLegacyHomeStorageKeys, getAllDataHomeStorageKey, setJsonStorageValue } from '../utils/homeCache';
import { runWhenIdle } from '../utils/idle';
import { unificarTiposHomonimosDoSite } from '../utils/tiposHomonimos';
import { isLikelyMetaPayloadText } from '../utils/isLikelyMetaPayloadText';
import { buildImoveisSeo } from '../utils/imoveisSeo';
import { descricaoListagemPorContexto, temSeoContextoListagem, tituloListagemPorContexto } from '../utils/seoContextoListagem';
import { resolvePixelId, ensureBrowserPixel, sendMetaCapiEvent } from '../utils/metaPixel';
import {
  ALLOW_MARKETING_SRC_PREFIX,
  MARKETING_SNIPPET_MARKERS,
  type MarketingSnippetMarker,
  prepareMarketingSnippets,
  renderMarketingSnippetElements,
  type PreparedMarketingSnippet,
  type SiteMarketingSettings,
} from '../utils/marketingSnippets';
import { buildCanonical, isLocalOrTestHost, normalizeHost } from '../utils/url';
import { normalizarCaminhoListagem } from '../utils/legacy-redirects';
import { anexaUrlDaPaginaNoWhats, ehLinkDeWhatsapp, siteAnexaUrlDaPaginaNoWhats } from '../utils/urlPaginaNoWhats';
import { anexaUtmNoWhats, guardaUtmDaPrimeiraVisita, UTM_NO_WHATS_ATIVO } from '../utils/utmNoWhats';
import { temSeletorIdiomas } from '../utils/idiomaMoedaSite';
import { siteUsaFormularioExternoNoWhats } from '../utils/formularioExternoNoWhats';
import { shouldUsePartytown } from '../utils/partytown';
import { initBuilderBridge } from '../lib/builder-bridge';

// MULTI-FILIAIS: popup de escolha de filial. client-only (depende de cookie e
// localStorage — nada disso pode entrar no HTML do SSR/CDN) e ele mesmo devolve
// null quando o tenant não está no modo popup, então nem monta pros outros.
const FilialPopup = dynamic(() => import('../components/fixed/filiais/FilialPopup'), { ssr: false });
// cs2431: formulário do Bitrix24 antes do WhatsApp — o chunk só é baixado pelos codsites do mapa.
const FormularioExternoNoWhats = dynamic(() => import('../components/fixed/formularioExternoNoWhats/FormularioExternoNoWhats'), { ssr: false });
// 22/10: widget de idioma (GTranslate) ESCONDIDO + `?lang=` na URL — a escolha mora no menu de cada
// modelo (substituiu a pílula flutuante de 21/10). Client-only e só montado nos tenants com a opção do
// CRM ligada (utils/idiomaMoedaSite): o SSR e o chunk dos demais não mudam.
const IdiomaOculto = dynamic(() => import('../components/pieces/idiomas/IdiomaOculto'), { ssr: false });
// carrega o chunk da Avenir só quando for preciso

// carregue as “substitutas” com variável
const manrope = Manrope({
  subsets: ['latin'],
  weight: ['200','500', '700'], // corpo e destaque
  display: 'swap',
});

const robotoCondensed = Roboto_Condensed({ subsets: ['latin'], weight: ['300', '400', '700'], display: 'swap' });
const roboto = Roboto({ subsets: ['latin'], weight: ['300', '700'], display: 'swap' });
const syncopate = Syncopate({ subsets: ['latin'], weight: ['400', '700'], display: 'swap' });

// NUNITO SANS (Google) — pedido do cs2631 (09/09): a fonte do site inteiro passa a ser a
// Nunito Sans. Gate por codsite, como a Avenir do 1722/2379: só o tenant da allowlist recebe
// a família em `--fonte-principal` (é a variável que todo o CSS dos modelos usa). Fonte
// variável (um arquivo, todos os pesos que os componentes pedem) e `preload: false` para os
// outros ~300 sites não pagarem o preload de uma fonte que não usam — o @font-face entra no
// CSS, mas o navegador só baixa o arquivo se algum elemento usar a família.
const nunitoSans = Nunito_Sans({ subsets: ['latin'], display: 'swap', preload: false });
const NUNITO_SANS_FAMILY = `'Nunito Sans', 'Nunito Sans Fallback', sans-serif`;
const NUNITO_SANS_CODSITES = new Set<number>([2631]);
const usaNunitoSans = (site: any): boolean => NUNITO_SANS_CODSITES.has(Number(site?.id));


/* ------------------------------------------------------------------
 *  _app.tsx – versão refatorada para eliminar avisos ESLint
 * ------------------------------------------------------------------ */

const EMPTY_SITE_SETTINGS: SiteMarketingSettings = {};
const ALLOW_SRC_PREFIX = ALLOW_MARKETING_SRC_PREFIX;

type IpResponse = { ip: string };

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

async function getClientIp(): Promise<string> {
  const ip6 = await fetchJson<IpResponse>('https://api6.ipify.org?format=json');
  if (ip6?.ip) return ip6.ip;
  const ip4 = await fetchJson<IpResponse>('https://api.ipify.org?format=json');
  return ip4?.ip || '0.0.0.0';
}

async function getEventId(): Promise<string> {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  const { v4 } = await import('uuid');
  return v4();
}

// Modo teste de pixel (?fbteste=1): contrato ÚNICO, consumido pelo gate de marketing e
// pelo effect do Facebook — mudar aqui muda nos dois. Só é avaliado na carga inicial
// (as ferramentas da Meta sempre abrem a URL completa com o param).
function isFbTestMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('fbteste') === '1';
  } catch {
    return false;
  }
}

function toPositiveInt(value: unknown): number {
  const source = Array.isArray(value) ? value[0] : value;
  const parsed = Number(String(source ?? '').replace(/[^\d]/g, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
}

function isSameSiteCache(data: any, expectedHost: string, expectedCodsite: number): boolean {
  if (!data?.site?.id) return false;

  const expectedHostNormalized = normalizeHost(expectedHost);
  const cachedHostNormalized = normalizeHost(data?.site?.site);
  const stripWww = (host: string) => host.replace(/^www\./i, '');

  const codsiteMatches = expectedCodsite <= 0 || Number(data?.site?.id) === expectedCodsite;
  const hostMatches =
    !expectedHostNormalized ||
    isLocalOrTestHost(expectedHostNormalized) ||
    !cachedHostNormalized ||
    stripWww(cachedHostNormalized) === stripWww(expectedHostNormalized);

  return codsiteMatches && hostMatches;
}

function removeDynamicInjectedNodes(markers: readonly MarketingSnippetMarker[] = MARKETING_SNIPPET_MARKERS): void {
  if (typeof window === 'undefined') return;

  const activeMarkers = new Set(markers);
  document
    .querySelectorAll<HTMLElement>('[data-injected-dynamic="1"][data-injected]')
    .forEach((node) => {
      const marker = node.getAttribute('data-injected') as MarketingSnippetMarker | null;
      if (!marker || !activeMarkers.has(marker)) return;
      node.remove();
    });
}

function injectHtml(
  html: string,
  target: 'head' | 'body',
  marker: MarketingSnippetMarker,
  usePartytown = false
) {
  if (typeof window === 'undefined' || !html) return;

  const root = target === 'head' ? document.head : document.body;

  // limpa versões anteriores deste "grupo"
  root
    .querySelectorAll<HTMLElement>(`[data-injected="${marker}"][data-injected-dynamic="1"]`)
    .forEach((node) => node.remove());

  const container = document.createElement('div');
  container.innerHTML = html;

  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'script') {
        // bloqueia fontes não permitidas
      const s = document.createElement('script');
      // copia atributos (async, defer, src, type, etc.)
      Array.from(el.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      if (el.textContent) s.text = el.textContent;
      // Partytown (gated): roda o GTM no web worker em vez da main-thread (TBT).
      if (usePartytown) s.setAttribute('type', 'text/partytown');
      s.setAttribute('data-injected', marker);
      s.setAttribute('data-injected-dynamic', '1');
      root.appendChild(s);
      return;
    }

    // <noscript>, <link>, <meta>, <style>, etc.
    const clone = el.cloneNode(true) as HTMLElement;
    clone.setAttribute('data-injected', marker);
    clone.setAttribute('data-injected-dynamic', '1');
    root.appendChild(clone);
  });
}

type ValidationResult = { valid: boolean; errors: string[] };

// eslint-disable-next-line no-unused-vars
function hasGtmSnippet(html: string | null | undefined): boolean {
  if (!html) return false;
  return /googletagmanager\.com\/gtm\.js|GTM-[A-Z0-9]+|dataLayer/i.test(html);
}

// eslint-disable-next-line no-unused-vars
function validateSnippet(html: string | null | undefined, expectScript = true): ValidationResult {
  if (!html || typeof html !== "string") return { valid: false, errors: ["snippet vazio"] };

  const errors: string[] = [];
  const container = document.createElement("div");
  container.innerHTML = html;

  // 1) scripts
  const scripts = Array.from(container.querySelectorAll("script"));
  if (expectScript && scripts.length === 0) {
    errors.push("nenhuma <script> encontrada");
  }

  for (const el of scripts) {
    const src = el.getAttribute("src");
    const type = (el.getAttribute("type") || "").toLowerCase();

    // ld+json é OK no <head> (não executa JS)
    if (type && type !== "text/javascript" && type !== "application/javascript" && type !== "module" && type !== "application/ld+json") {
      errors.push(`type inesperado no <script>: ${type}`);
      continue;
    }

    if (src) {
      if (!/^https:\/\//i.test(src)) errors.push(`src inválido (exige https): ${src}`);
      if (!ALLOW_SRC_PREFIX.some((p) => src.startsWith(p))) errors.push(`domínio não permitido: ${src}`);
    } else {
      const code = (el.textContent || "").trim();
      if (!code && type !== "application/ld+json") {
        errors.push("script inline vazio");
      } else if (type !== "application/ld+json") {
        try {
          // valida sintaxe sem executar
          // eslint-disable-next-line no-new-func
          new Function(code);
        } catch (e: any) {
          errors.push(`JS inline com erro de sintaxe: ${String(e?.message || e)}`);
        }
      }
    }
  }

  // 2) noscript + iframe (GTM)
  const noscriptIframes = Array.from(container.querySelectorAll("noscript iframe"));
  for (const iframe of noscriptIframes) {
    const src = iframe.getAttribute("src") || "";
    if (!ALLOW_SRC_PREFIX.some((p) => src.startsWith(p))) {
      errors.push(`<noscript><iframe> com domínio não permitido: ${src}`);
    }
  }

  // 3) tags não esperadas no snippet (endurece um pouco)
  const allowedTags = new Set(["SCRIPT", "NOSCRIPT", "LINK", "META", "STYLE", "IFRAME", "#comment"]);
  const weirdTags = Array.from(container.children).filter((el) => !allowedTags.has(el.tagName));
  if (weirdTags.length) {
    errors.push(`tags não permitidas: ${weirdTags.map((n) => n.tagName.toLowerCase()).join(", ")}`);
  }

  return { valid: errors.length === 0, errors };
}

const HIDDEN_META_PAYLOAD_ATTR = "data-hidden-meta-payload";

function isSkippableTextParent(element: Element | null): boolean {
  if (!element) return true;
  const tag = element.tagName.toUpperCase();
  return tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT" || tag === "TEXTAREA";
}

function hideMetaPayloadTextNodes(root: any): void {
  if (typeof window === "undefined") return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const toHide: Text[] = [];

  let current = walker.nextNode();
  while (current) {
    const textNode = current as Text;
    const parent = textNode.parentElement;
    const content = textNode.nodeValue || "";

    if (
      content.trim() &&
      !isSkippableTextParent(parent) &&
      isLikelyMetaPayloadText(content)
    ) {
      toHide.push(textNode);
    }

    current = walker.nextNode();
  }

  for (const textNode of toHide) {
    const parent = textNode.parentElement;
    if (!parent || isSkippableTextParent(parent)) continue;

    const hidden = document.createElement("span");
    hidden.setAttribute(HIDDEN_META_PAYLOAD_ATTR, "1");
    hidden.setAttribute("aria-hidden", "true");
    hidden.style.display = "none";
    hidden.textContent = textNode.nodeValue || "";
    parent.replaceChild(hidden, textNode);
  }
}

function RenderHeadMarketingSnippets({
  snippets,
  usePartytown = false
}: {
  snippets: PreparedMarketingSnippet[];
  usePartytown?: boolean;
}) {
  const elements = snippets.flatMap((snippet) => renderMarketingSnippetElements(snippet, usePartytown));
  if (!elements.length) return null;

  return <Head>{elements}</Head>;
}

function RenderBodyMarketingSnippets({
  snippets,
  usePartytown = false
}: {
  snippets: PreparedMarketingSnippet[];
  usePartytown?: boolean;
}) {
  if (!snippets.length) return null;

  return <>{snippets.flatMap((snippet) => renderMarketingSnippetElements(snippet, usePartytown))}</>;
}

function InjectMarketingSnippets({
  snippets,
  enabled,
  usePartytown = false
}: {
  snippets: PreparedMarketingSnippet[];
  enabled: boolean;
  usePartytown?: boolean;
}) {
  useEffect(() => {
    if (!enabled) {
      removeDynamicInjectedNodes();
      return;
    }

    const activeMarkers = new Set(snippets.map((snippet) => snippet.marker));
    const inactiveMarkers = MARKETING_SNIPPET_MARKERS.filter((marker) => !activeMarkers.has(marker));
    if (inactiveMarkers.length > 0) {
      removeDynamicInjectedNodes(inactiveMarkers);
    }

    for (const snippet of snippets) {
      const hasDeclarativeMarkup = Boolean(
        document.querySelector(
          `[data-injected="${snippet.marker}"]:not([data-injected-dynamic="1"])`
        )
      );

      if (hasDeclarativeMarkup) {
        removeDynamicInjectedNodes([snippet.marker]);
        continue;
      }

      injectHtml(snippet.html, snippet.target, snippet.marker, usePartytown);
    }
  }, [enabled, snippets, usePartytown]);

  return null;
}

// Controle de pageview (gtag ou GTM)
function TrackPageView({ enabled }: { enabled: boolean }) {
  const router = useRouter();

  useEffect(() => {
    if (!enabled) return;

    const handle = (url: string) => {
      // GA4 via gtag.js (se presente)
      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "page_view", {
          page_path: url,
          page_location: window.location.origin + url,
          page_title: document.title,
        });
      }
      // GTM (se preferir ouvir event=pageview no GTM)
      if (typeof window !== "undefined" && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: "pageview",
          page_path: url,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    };

    // Não envia o primeiro PV aqui para evitar duplicidade
    router.events.on("routeChangeComplete", handle);
    return () => router.events.off("routeChangeComplete", handle);
  }, [enabled, router.events]);

  return null;
}

// BUILDER: injeta o CSS custom do tenant como <style data-tenant-custom> — via
// next/head pra sair no HTML do SSR (funciona sem JS/hidratação). Preview
// (?tema=css:) e publicado (customCss) chegam JÁ sanitizados do servidor
// (ver lib/server/theme-preview.ts e site-props.ts loadCustomCss); aqui é só
// o ponto único de injeção. Sem CSS → não renderiza nada.
function RenderTenantCustomCss({ css }: { css: string | null | undefined }) {
  if (!css) return null;
  return (
    <Head>
      {/* eslint-disable-next-line react/no-danger */}
      <style data-tenant-custom dangerouslySetInnerHTML={{ __html: css }} />
    </Head>
  );
}

function TrackSite2434WhatsAppLead({ enabled }: { enabled: boolean }) {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const whatsappTarget = target?.closest(
        [
          'a[href*="wa.me"]',
          'a[href*="api.whatsapp.com"]',
          'a[href*="web.whatsapp.com"]',
          'button[data-whatsapp]',
          '.btn-whatsapp',
          '.whatsapp',
          '[class*="whatsapp"]',
        ].join(', ')
      );

      if (whatsappTarget && typeof (window as any).fbq !== 'undefined') {
        (window as any).fbq('track', 'Lead');
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [enabled]);

  return null;
}

// URL DA PÁGINA NA MENSAGEM DO WHATSAPP — só para os codsites da allowlist (utils/urlPaginaNoWhats).
//
// Ponto ÚNICO, no lugar de tocar nos 31 arquivos que montam link de WhatsApp. Precisa das
// DUAS pernas porque o projeto abre WhatsApp de dois jeitos:
//
//   (a) <a href> que navega sozinho — 12 arquivos (MegaFooter, TeaserModal, DecoradorModal,
//       ManagerGrid, WelcomePieces, blog, empreendimento…). Reescrevemos o href na fase de
//       CAPTURA do document, que roda antes de qualquer onClick do React (React 18 escuta no
//       container #__next, descendente do document).
//
//   (b) window.open(variavelJS) — 18 arquivos, ~55 pontos. É o padrão dominante: o onClick faz
//       e.preventDefault() e depois setTimeout(() => window.open(...)). Aí o href do DOM é
//       IGNORADO — a URL vem de uma template string recalculada em JS. Sem este patch, a
//       maioria dos botões do site ficaria de fora.
//
// A reescrita (anexaUrlDaPaginaNoWhats) é idempotente: recorta um sufixo anterior antes de
// anexar o novo. Assim clique repetido, re-render do React ou filtro alterado nunca duplicam
// a linha — e o href reescrito no DOM não fica "grudado" numa URL velha.
function UrlDaPaginaNoWhats({ enabled }: { enabled: boolean }) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof document === 'undefined') return;

    // Lido NO MOMENTO do clique, não no render: os filtros da listagem navegam com
    // router.push(..., { shallow: true }) (pages/imoveis/index.tsx), que é history.pushState —
    // a querystring já está aqui, mesmo sem re-render do componente do botão.
    const urlDaPagina = () => window.location.href;

    /* (a) href ------------------------------------------------------------- */
    const aoInteragir = (evento: Event) => {
      const alvo = evento.target instanceof Element ? evento.target : null;
      const ancora = alvo?.closest('a[href]') as HTMLAnchorElement | null;
      if (!ancora) return;

      const atual = ancora.href; // href absoluto, já normalizado pelo browser
      if (!ehLinkDeWhatsapp(atual)) return;

      const novo = anexaUrlDaPaginaNoWhats(atual, urlDaPagina());
      if (novo !== atual) ancora.href = novo;
    };

    // pointerdown pega mouse/toque/caneta, botão do meio e "abrir em nova aba" pelo menu de
    // contexto (que não dispara click); click cobre o Enter no teclado.
    document.addEventListener('pointerdown', aoInteragir, true);
    document.addEventListener('click', aoInteragir, true);

    /* (b) window.open ------------------------------------------------------ */
    const openAnterior = window.open;
    const jaPatchado = (window.open as any)?.__urlPagina === true;

    if (!jaPatchado) {
      const openComUrlDaPagina: any = function (url?: any, target?: any, features?: any) {
        const destino = typeof url === 'string' ? url : url instanceof URL ? url.href : '';
        const final =
          destino && ehLinkDeWhatsapp(destino)
            ? anexaUrlDaPaginaNoWhats(destino, urlDaPagina())
            : url;
        // chamada SÍNCRONA e direta: não perde o "user activation" (popup blocker).
        return openAnterior.call(window, final, target, features);
      };
      openComUrlDaPagina.__urlPagina = true;       // trava contra patch duplo (StrictMode/remount)
      openComUrlDaPagina.__openOriginal = openAnterior;
      window.open = openComUrlDaPagina;
    }

    return () => {
      document.removeEventListener('pointerdown', aoInteragir, true);
      document.removeEventListener('click', aoInteragir, true);
      // só devolve se o window.open ainda for o NOSSO — se alguém patchou por cima depois de
      // nós, restaurar aqui apagaria o patch do outro.
      const atual: any = window.open;
      if (atual?.__urlPagina === true) window.open = atual.__openOriginal || openAnterior;
    };
  }, [enabled]);

  return null;
}

// UTM DA CAMPANHA NO BOTÃO DE WHATSAPP — TODOS os sites (utils/utmNoWhats, 22/10/2026).
//
// Guarda a UTM da 1ª visita (30 dias) e, no clique, cola na mensagem o código invisível que o
// worker do CRM lê para gravar a origem do lead (campanha, ou "site" sem campanha). Mesmas DUAS
// pernas do UrlDaPaginaNoWhats acima — <a href> na fase de captura e window.open — e os dois
// convivem em qualquer ordem: cada reescrita é idempotente e mexe só no que é seu (o código
// invisível aqui, a linha "Página que estou vendo" lá).
function UtmNoWhats() {
  useEffect(() => {
    if (!UTM_NO_WHATS_ATIVO || typeof window === 'undefined' || typeof document === 'undefined') return;

    guardaUtmDaPrimeiraVisita(window.location.href);
    // Lida NO CLIQUE: a página atual pode ter chegado com UTM por navegação client-side.
    const utmAgora = () => guardaUtmDaPrimeiraVisita(window.location.href);

    const aoInteragir = (evento: Event) => {
      const alvo = evento.target instanceof Element ? evento.target : null;
      const ancora = alvo?.closest('a[href]') as HTMLAnchorElement | null;
      if (!ancora) return;
      const atual = ancora.href;
      if (!ehLinkDeWhatsapp(atual)) return;
      const novo = anexaUtmNoWhats(atual, utmAgora());
      if (novo !== atual) ancora.href = novo;
    };
    document.addEventListener('pointerdown', aoInteragir, true);
    document.addEventListener('click', aoInteragir, true);

    const openAnterior = window.open;
    const jaPatchado = (window.open as any)?.__utmWhats === true;
    if (!jaPatchado) {
      const openComUtm: any = function (url?: any, target?: any, features?: any) {
        const destino = typeof url === 'string' ? url : url instanceof URL ? url.href : '';
        const final = destino && ehLinkDeWhatsapp(destino) ? anexaUtmNoWhats(destino, utmAgora()) : url;
        // chamada SÍNCRONA e direta: não perde o "user activation" (popup blocker).
        return openAnterior.call(window, final, target, features);
      };
      openComUtm.__utmWhats = true;
      openComUtm.__openOriginal = openAnterior;
      window.open = openComUtm;
    }

    return () => {
      document.removeEventListener('pointerdown', aoInteragir, true);
      document.removeEventListener('click', aoInteragir, true);
      const atual: any = window.open;
      if (atual?.__utmWhats === true) window.open = atual.__openOriginal || openAnterior;
    };
  }, []);

  return null;
}

function MyApp({ Component, pageProps }: any) {
    /* --------------------- ESTADO ---------------------------------- */
  const router = useRouter();
    const props = pageProps || {};
    const asPath = props?.asPath || router.asPath || '/';
    const expectedHost = useMemo(() => {
      const fallbackHost =
        props?.prodOrigin ||
        props?.origemInit ||
        (typeof window !== 'undefined' ? window.location.host : '');
      return normalizeHost(fallbackHost);
    }, [props?.origemInit, props?.prodOrigin]);
    const expectedCodsite = useMemo(() => toPositiveInt(props?.codsite), [props?.codsite]);
    const allDataHomeCacheKey = useMemo(
      () => getAllDataHomeStorageKey(expectedHost, expectedCodsite),
      [expectedHost, expectedCodsite]
    );
    const canonicalUrl = useMemo(
      () => props?.canonical || buildCanonical(props?.canonicalHost || props?.prodOrigin, asPath || '/'),
      [props?.canonical, props?.canonicalHost, props?.prodOrigin, asPath]
  );
    const [allDataHome, setAllDataHome] = useState<any>(props?.allDataHome);
    const [site, setSite] = useState<any>(props?.allDataHome?.site);
    const [origem, setOrigem] = useState<any>(
        props?.origemInit || props?.prodOrigin || (typeof window !== 'undefined' ? window.location.host : '')
    );
    const notFound = props?.notFound ?? false;
    const siteCongelado = props?.siteCongelado === true || props?.erro === false || props?.allDataHome?.site?.congelado === true;
    const hasGoogleMarketingSnippetSource = Boolean(
      site?.tag_google_head ||
      site?.tag_google_body ||
      site?.google_analytics_head ||
      site?.google_analytics_body
    );
    const hasMetaPixelSource = Boolean(site?.pixel_facebook_head) || Boolean(resolvePixelId(site?.fb_id_pixel, site?.pixel_facebook_head));
    // Adia TODO o JS de terceiros (Google GTM/GA4/Ads + Meta Pixel) até a 1ª interação
    // do usuário OU idle. Tira o marketing do caminho crítico (LCP/TBT) sem perder o
    // rastreio: SEMPRE executa (o fallback por idle garante). Vale p/ desktop e mobile.
    const [marketingReady, setMarketingReady] = useState(false);
    useEffect(() => {
      if (marketingReady) return;
      // ?fbteste=1 (teste de pixel): fura o gate NA CARGA — Test Events/Pixel Helper da Meta
      // precisam ver GTM/GA4/pixel imediatamente, inclusive pixel instalado DENTRO do GTM.
      // Lighthouse nunca inclui o param (nota intacta). Referrer do facebook NÃO fura o gate
      // global de propósito: tráfego real de anúncio não paga o marketing na carga (o effect
      // do pixel FB tem bypass próprio p/ esse caso).
      // fb_pixel_teste (flag por tenant no tbl_sites, ligável pelo CRM) = ?fbteste=1 PERSISTENTE:
      // a Ferramenta de Configuração de Eventos/Test Events da Meta abre a URL do site SEM o
      // param, então o flag garante o pixel na carga enquanto o modo teste estiver ligado.
      if (isFbTestMode() || site?.fb_pixel_teste) {
        setMarketingReady(true);
        return;
      }
      let fired = false;
      const fire = () => {
        if (fired) return;
        fired = true;
        teardown();
        setMarketingReady(true);
      };
      const evs = [
        'pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll', 'mousemove',
      ];
      // capture:false torna o objeto compatível tanto com addEventListener
      // (AddEventListenerOptions) quanto com removeEventListener (EventListenerOptions).
      const opts = { once: true, passive: true, capture: false };
      evs.forEach((e) => window.addEventListener(e, fire, opts));
      // Fallback por TEMPO FIXO (NÃO requestIdleCallback — esse dispara cedo quando a
      // thread ocia e entraria na janela de medição do Lighthouse). 8s fica depois do
      // load/TTI típico: não pesa na nota, mas garante execução p/ quem não interage.
      const fallbackTimer = window.setTimeout(fire, 8000);
      function teardown() {
        evs.forEach((e) => window.removeEventListener(e, fire, opts));
        window.clearTimeout(fallbackTimer);
      }
      return teardown;
    }, [marketingReady]);
    const preparedMarketingSnippets = useMemo(
      () =>
        prepareMarketingSnippets(
          (site as SiteMarketingSettings | null) || EMPTY_SITE_SETTINGS,
          hasGoogleMarketingSnippetSource
        ),
      [hasGoogleMarketingSnippetSource, site]
    );
    const canTrackGooglePageViews = preparedMarketingSnippets.valid.length > 0;
    // POC Partytown gated na cobaia: roda o GTM no web worker. MESMO critério (host)
    // do loader no _document — senão o GTM viraria text/partytown sem o loader (=GTM morto).
    const usePartytown = shouldUsePartytown(expectedHost, props?.codsite);
    // Nunca emitir o marketing declarativo (nem no SSR, nem pós-interação): o caminho
    // imperativo InjectMarketingSnippets força a execução dos <script> de forma confiável
    // e adiada (gateado por marketingReady). Assim o GTM/GA sai do <head> do HTML servido.
    const shouldRenderInitialMarketingMarkup = false;
    const initialRenderMarketingSnippets = useMemo(
      () =>
        shouldRenderInitialMarketingMarkup
          ? preparedMarketingSnippets.valid
          : [],
      [preparedMarketingSnippets.valid, shouldRenderInitialMarketingMarkup]
    );
    const initialHeadMarketingSnippets = useMemo(
      () => initialRenderMarketingSnippets.filter((snippet) => snippet.target === 'head'),
      [initialRenderMarketingSnippets]
    );
    const initialBodyMarketingSnippets = useMemo(
      () => initialRenderMarketingSnippets.filter((snippet) => snippet.target === 'body'),
      [initialRenderMarketingSnippets]
    );
    // BUILDER: preview (?tema=css:, só host de teste) tem PRIORIDADE sobre o CSS
    // custom publicado — mesma regra de precedência dos demais campos do ?tema=.
    const effectiveCustomCss = useMemo(
      () => props?.themePreview?.customCss || props?.customCss || null,
      [props?.themePreview?.customCss, props?.customCss]
    );
    // PRÉVIA DO CRM (?tema=video:parado / filiais:fechado, só host de teste): false p/ visitante real.
    const previaBuilder = useMemo(
      () => ({
        videoParado: Boolean(props?.themePreview?.videoParado),
        filiaisFechado: Boolean(props?.themePreview?.filiaisFechado),
      }),
      [props?.themePreview?.videoParado, props?.themePreview?.filiaisFechado]
    );

    useEffect(() => {
        if (props?.allDataHome) {
            setAllDataHome(props.allDataHome);
            setSite(props.allDataHome?.site);
        }

        const origemFromProps =
            props?.origemInit ||
            props?.prodOrigin ||
            props?.allDataHome?.site?.site;

        if (origemFromProps) {
            setOrigem(origemFromProps);
        }
    }, [props?.allDataHome, props?.origemInit, props?.prodOrigin]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        clearLegacyHomeStorageKeys(window.localStorage);
    }, []);

    // BUILDER: canal postMessage com o painel do Builder do CRM — só ativa em
    // host de teste com ?builder=1 (ver lib/builder-bridge.ts, mesmo gate
    // client-safe usado pelo ?codsite/?tema). Roda uma vez por navegação de
    // página (o _app remonta entre navegações client-side normais do Next
    // porque MyApp em si não desmonta, mas os listeners são idempotentes e o
    // cleanup remove tudo se o componente desmontar).
    useEffect(() => {
        return initBuilderBridge();
    }, []);

    useEffect(() => {
        if (!hasGoogleMarketingSnippetSource) return;

        preparedMarketingSnippets.invalid.forEach((warning) => {
            console.warn(`[inject:${warning.marker}] bloqueado:`, warning.errors);
        });
    }, [hasGoogleMarketingSnippetSource, preparedMarketingSnippets.invalid]);

    useEffect(() => {
        if (site?.id) {
            document.documentElement.dataset.siteId = String(site.id);
            document.body.dataset.siteId = String(site.id);
        }
    }, [site?.id]);

    useEffect(() => {
        if(site){
            document.documentElement.style.setProperty('--cor1', site?.cor1 || '#1f1f1f');
            document.documentElement.style.setProperty('--cor2', site?.cor2 || '#77aa4d');
            document.documentElement.style.setProperty('--cor3', site?.cor3 || '#202020');
            document.documentElement.style.setProperty('--cor4', site?.cor4 || '#181818');
            document.documentElement.style.setProperty('--cor5', site?.cor5 || '#ffffff');
            document.documentElement.style.setProperty('--cor6', site?.cor6 || '#000000');
            document.documentElement.style.setProperty('--cor7', site?.cor7 || '#ffffff');
            document.documentElement.style.setProperty('--cor8', site?.cor8 || '#ffffff');
            document.documentElement.style.setProperty('--cor9', site?.cor9 || '#181818');
            document.documentElement.style.setProperty('--cor10', site?.cor10 || '#363636');
            document.documentElement.style.setProperty('--cor11', site?.cor11 || '#1a1a1a');
            document.documentElement.style.setProperty('--cor12', site?.cor12 || '#1a1a1a');
            document.documentElement.style.setProperty('--cor13', site?.cor13 || '#ffffff');
            document.documentElement.style.setProperty('--cor14', site?.cor14 || '#494949');
            document.documentElement.style.setProperty('--cor15', site?.cor15 || '#000000');
            if(isColorLight(site?.cor2)){
                document.documentElement.style.setProperty('--cor_font2', site?.cor11 || '#1a1a1a');
            } else {
                document.documentElement.style.setProperty('--cor_font2', site?.cor13 || '#ffffff');
            }
            if((isColorLight(site?.cor2) && isColorLight(site?.cor5)) || (!isColorLight(site?.cor2) && !isColorLight(site?.cor5))){
                if(isColorLight(site?.cor5)){
                    document.documentElement.style.setProperty('--cor_title', site?.cor11 || '#1a1a1a');
                } else {
                    document.documentElement.style.setProperty('--cor_title', site?.cor13 || '#ffffff');
                }
            } else {
                document.documentElement.style.setProperty('--cor_title', site?.cor2 || '#ffffff');
            }
            if(isColorLight(site?.cor5)){
                document.documentElement.style.setProperty('--cor_font', site?.cor11 || '#1a1a1a');
            } else {
                document.documentElement.style.setProperty('--cor_font', site?.cor13 || '#ffffff');
            }
            if(isColorLight(site?.cor15)){
                document.documentElement.style.setProperty('--cor_font15', site?.cor11 || '#1a1a1a');
            } else {
                document.documentElement.style.setProperty('--cor_font15', site?.cor13 || '#ffffff');
            }
        }

        if(site?.id == 1722 || site?.id == 2379){
            document.documentElement.style.setProperty(
                '--fonte-principal',
                `'avenirBook', 'avenirBook Fallback'`
            );
        }
        // cs2631: cobre o que fica FORA do <main> (portais/modais no body) — dentro dele a
        // variável já vem inline no SSR, sem trocar de fonte depois do primeiro paint.
        if (usaNunitoSans(site)) {
            document.documentElement.style.setProperty('--fonte-principal', NUNITO_SANS_FAMILY);
        }
    }, [site]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        // 1ª passada imediata (no próximo frame). Depois, as mutações são
        // COALESCIDAS num único scan a cada 200ms — antes rodava um TreeWalker
        // no <body> inteiro a cada frame com mutação (custo de main-thread/TBT
        // alto durante a montagem dos carrosséis na home).
        let timer: ReturnType<typeof setTimeout> | null = null;
        const run = () => {
            timer = null;
            hideMetaPayloadTextNodes(document.body);
        };
        const schedule = () => {
            if (timer) return;
            timer = setTimeout(run, 200);
        };

        const rafId = window.requestAnimationFrame(() => {
            hideMetaPayloadTextNodes(document.body);
        });

        const observer = new MutationObserver(schedule);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true
        });

        return () => {
            if (timer) clearTimeout(timer);
            window.cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, []);

    // 2) 404 SSR – faz o redirect de forma estável (evita hook condicional)
    useEffect(() => {
        if (notFound && asPath !== '/404') {
        router.replace('/404');
        }
    }, [notFound, asPath, router]);

    // 3) Site congelado → página de marketing
    useEffect(() => {
        if (siteCongelado) {
        router.replace('https://www.imobtotal.com.br/congelado.html');
        }
    }, [siteCongelado, router]);

    /* --------------------- LOGS  ----------------------------------- */
    const salvaLog = useCallback(async () => {
        try {
        const ip = await getClientIp();
        await salvaLogs(`${origem}${asPath}`, ip, props?.codsite, origem);
        } catch {
        await salvaLogs(`${origem}${asPath}`, '0', props?.codsite, origem);
        }
    }, [asPath, origem, props?.codsite]);

    // Chama o log quando ativado
    useEffect(() => {
        if (!site?.ativa_logs || !allDataHome) return;
        const cancelIdle = runWhenIdle(() => {
            void salvaLog();
        }, 1200);
        return cancelIdle;
    }, [site?.ativa_logs, allDataHome, salvaLog]);

   // extraia a função para poder memorizá-la
    const getInfoSite = useCallback(async () => {
        try {
            const cachedRaw =
              localStorage.getItem(allDataHomeCacheKey) || localStorage.getItem('allDataHome');
            if (cachedRaw) {
                // Mesma unificação de tipos do SSR (cs2644): este caminho recarrega o payload cru.
                const data = unificarTiposHomonimosDoSite(JSON.parse(cachedRaw));
                if (isSameSiteCache(data, expectedHost, expectedCodsite)) {
                    setJsonStorageValue(localStorage, allDataHomeCacheKey, data);
                    localStorage.removeItem('allDataHome');
                    setAllDataHome(data);
                    setSite(data?.site);
                    setOrigem(data?.site?.site || origem || window.location.host);
                    return;
                }
            }

            const originToFetch = origem || window.location.host;
            const data = unificarTiposHomonimosDoSite(await allDataHomePage(originToFetch, props?.codsite));
            if (!data?.site?.id) return;

            setJsonStorageValue(localStorage, allDataHomeCacheKey, data);
            localStorage.removeItem('allDataHome');
            setAllDataHome(data);
            setSite(data?.site);
            setOrigem(data?.site?.site || originToFetch);
        } catch (e) {
            console.error('Erro ao buscar dados:', e);
            void salvaLog();
        }
    }, [allDataHomeCacheKey, expectedCodsite, expectedHost, origem, props?.codsite, salvaLog]);

    // efeito
    useEffect(() => {
        if (!allDataHome) {
            void getInfoSite();
            return;
        }
        setJsonStorageValue(localStorage, allDataHomeCacheKey, allDataHome);
        localStorage.removeItem('allDataHome');
    }, [allDataHome, allDataHomeCacheKey, getInfoSite]);

    const fbPixelInitialized = useRef<boolean>(false);
    const pageViewSentRef = useRef<boolean>(false);
    const routeHandlerRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        // Contexto do Facebook (ferramenta "Add Events"/verificação da Meta ou tráfego vindo do
        // FB): dispara o pixel NA CARGA p/ a Meta detectar o PageView. Sinais: ?fbteste=1 na URL
        // (use ao rodar o Add Events) OU referrer = facebook.com OU flag fb_pixel_teste do tenant
        // (modo teste ligado no CRM — cobre a Ferramenta de Config de Eventos que abre a URL sem
        // o param). Usuário normal/orgânico segue ADIADO (marketingReady, gate de 8s) → PageSpeed intacto.
        let fbContext = isFbTestMode() || !!site?.fb_pixel_teste;
        try {
            if (!fbContext && document.referrer) {
                const refHost = new URL(document.referrer).hostname;
                if (/(^|\.)facebook\.com$/i.test(refHost)) fbContext = true;
            }
        } catch {
            /* sem referrer válido → ignora */
        }
        // Crawler/verificador de pixel da Meta (facebookexternalhit etc.): a ferramenta
        // "detectar pixel" da Meta carrega o domínio PURO (sem ?fbteste=1) e espera só alguns
        // segundos — o adiamento de 8s do marketingReady esconderia o pixel e a Meta reportaria
        // "não foi possível detectar um pixel". Dispara NA CARGA p/ esses UAs (mesmo caminho do
        // ?fbteste=1). Exclui Lighthouse/PageSpeed de propósito → nota de performance intacta.
        try {
            const ua = navigator.userAgent || '';
            const isMetaCrawler = /facebookexternalhit|facebookcatalog|facebot|meta-externalagent/i.test(ua);
            const isPageSpeed = /Chrome-Lighthouse|Google Page Speed|Lighthouse/i.test(ua);
            if (isMetaCrawler && !isPageSpeed) fbContext = true;
        } catch {
            /* sem navigator → ignora */
        }

        if (!marketingReady && !fbContext) return; // adiado p/ usuário normal; FB dispara na carga
        if (!hasMetaPixelSource) return;
        // Pixel de browser precisa só de um Pixel ID (de fb_id_pixel OU extraído do snippet
        // legado pixel_facebook_head). É ele que a ferramenta "detectar pixel"/Test Events da
        // Meta procura (window.fbq). O CAPI, que exige o token, fica gated à parte lá embaixo —
        // assim tenant com fb_id_pixel mas sem pixel_facebook_head (ou vice-versa) é detectado.
        const resolvedPixelId = resolvePixelId(site?.fb_id_pixel, site?.pixel_facebook_head);
        if (!resolvedPixelId) return;

        const sendPageView = async () => {
            if (pageViewSentRef.current) return; // PageView inicial dispara UMA vez por carga
            if (!resolvedPixelId) return;
            pageViewSentRef.current = true;

            try {
                const eventId = await getEventId();

                // 1) Pixel BROWSER primeiro: é ele que o Pixel Helper/Test Events detectam, e o
                // JS do pixel não pode esperar rede (ipify/CAPI ficam DEPOIS). Base code CANÔNICO
                // via ensureBrowserPixel (NÃO react-facebook-pixel, cujo import()/init() falhava
                // silenciosamente no bundle → window.fbq nunca era criado). try/catch próprio:
                // qualquer falha aqui, o CAPI abaixo ainda envia.
                try {
                    // Dispara pra QUALQUER tenant com Pixel ID resolvível. O modelos-site NUNCA
                    // renderiza o snippet legado pixel_facebook_head, então usá-lo como gate deixava
                    // sem pixel de browser a classe "tem snippet no campo mas não tem GTM" (ex.: cs1822)
                    // — só o CAPI disparava e Pixel Helper/Event Setup Tool ficavam cegos. A proteção
                    // anti-duplicação de quem TEM GTM é o owned/ensureBrowserPixel (if window.fbq →
                    // return false), avaliado em RUNTIME — mais correto que o proxy estático do snippet.
                    if (resolvedPixelId && !fbPixelInitialized.current) {
                        // owned=true só quando NÓS criamos o window.fbq (ausente). Se já existia
                        // (ex.: GTM do tenant), não tocamos — evita duplicar PageView.
                        const owned = ensureBrowserPixel(resolvedPixelId);
                        if (owned) {
                            fbPixelInitialized.current = true;

                            // fbq() direto com o eventID (4º arg options) p/ a Meta dedupar browser×CAPI.
                            (window as any).fbq('track', 'PageView', {}, { eventID: eventId });

                            const handleRouteChange = async () => {
                                (window as any).fbq('track', 'PageView', {}, { eventID: await getEventId() });
                            };

                            router.events.on('routeChangeComplete', handleRouteChange);
                            // remove só no unmount real (não a cada re-run do efeito) p/ não perder o tracking de SPA
                            routeHandlerRef.current = () => router.events.off('routeChangeComplete', handleRouteChange);
                        }
                    }
                } catch {
                    // Sem pixel browser → segue pro CAPI mesmo assim.
                }

                // 2) CAPI depois, com o MESMO eventId (Meta dedupa browser×servidor). O token e o
                // Pixel ID são re-resolvidos server-side por host em /api/meta-capi (nunca no
                // cliente); aqui basta haver Pixel ID resolvível. sendMetaCapiEvent coleta
                // _fbp/_fbc/event_source_url sozinho e o IP real + User-Agent entram no servidor.
                if (!resolvedPixelId) return;
                sendMetaCapiEvent({ site, eventName: 'PageView', eventId });
            } catch {
                // Silencia falhas de rede de terceiros para não quebrar a navegação.
            }
        };

        // FB (?fbteste=1 ou referrer facebook) → chamada DIRETA, sem runWhenIdle: com timeout 0
        // o requestIdleCallback fica SEM deadline (só roda quando o browser ociar — segundos em
        // página pesada; em aba em background pode nem rodar). Usuário normal → idle pós-gate.
        if (fbContext) {
            void sendPageView(); // sem cleanup: pageViewSentRef já segura re-run/duplicata
            return;
        }
        const cancelIdle = runWhenIdle(() => {
            void sendPageView();
        }, 1200);

        return () => {
            cancelIdle?.();
        };
    }, [
        marketingReady,
        hasMetaPixelSource,
        router.events,
        site?.fb_id_pixel,
        site?.fb_token_pixel,
        site?.pixel_facebook_head,
        site?.fb_pixel_teste
    ]);

    // Remove o handler de routeChange só no unmount real do _app (o efeito acima re-roda quando
    // marketingReady vira true; não queremos perder o tracking de SPA nesse re-run).
    useEffect(() => {
        return () => {
            if (routeHandlerRef.current) {
                routeHandlerRef.current();
                routeHandlerRef.current = null;
            }
        };
    }, []);

    /* --------------------- HEAD HELPERS ---------------------------- */
    function RenderHeaderDetails() {
        return null;
    }

    function RenderImoveisDetails() {
        const renderOnlyTextFilters = () => {
            let aux = ""
             const { banheiros } = router.query;
            const { dormitorios } = router.query;
            const { suites } = router.query;
            const { vagas } = router.query;
            if(dormitorios && dormitorios != undefined){
                if(dormitorios == "1")
                    aux = " com 1 dormitório"
                else
                    aux = ` com ${dormitorios} dormitórios`
            } else {
                if(suites && suites != undefined){
                    if(suites == "1")
                        aux = " com 1 suíte"
                    else
                        aux = ` com ${suites} suítes`
                } else {
                    if(banheiros && banheiros != undefined){
                        if(banheiros == "1")
                            aux = " com 1 banheiro"
                        else
                            aux = ` com ${banheiros} banheiros`
                    } else {
                        if(vagas && vagas != undefined){
                            if(vagas == "1")
                                aux = " com 1 vaga"
                            else
                                aux = ` com ${vagas} vagas`
                        }
                    }
                }
            }
            const resposta = asPath.split("/")
            if(resposta[7]){
                return `Imóvel cod: ${resposta[7]}`
            }
            if(resposta[5] && resposta[5] != "-"){
                return `${decodeURIComponent(resposta[5].replaceAll("-"," "))} ${resposta[3] && resposta[3] != "-" ? ` em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}` : ""}${aux}`
            }

            let resposta_tipo = []
            if(resposta[6])
                resposta_tipo = resposta[6].split("?")
            if(resposta_tipo[0] != "-" && resposta_tipo[0]){
                if(resposta[3] && resposta[3] != "-"){
                    if(resposta[2] == "venda"){
                        return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} à venda em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                    } else {
                        if(resposta[2] == "aluguel"){
                            return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} para alugar em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                        }
                        if(resposta[2] == "temporada"){
                            return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} para temporada em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                        }
                        return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} em ${site?.Dados.cidade ? decodeURIComponent(site?.Dados.cidade) : null}`
                    }
                }
                return `${decodeURIComponent(resposta_tipo[0].replaceAll("-"," ").replaceAll("Casa", "Casas").replaceAll("Terreno", "Terrenos").replaceAll("Apartamento", "Apartamentos").replaceAll("Sobrado", "Sobrados").replaceAll("Cobertura", "Coberturas").replaceAll("+",", "))} para ${resposta[2]}${aux}`
            }
            if(resposta[3] && resposta[3] != "-"){
                if(resposta[2] == "venda"){
                    return `Imóveis à venda em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                } else {
                    if(resposta[2] == "aluguel"){
                        return `Imóveis para alugar em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                    }
                    if(resposta[2] == "temporada"){
                        return `Imóveis para temporada em ${decodeURIComponent(resposta[3].replaceAll("-"," "))}${aux}`
                    }
                    return `Imóveis em ${site?.Dados.cidade ? decodeURIComponent(site?.Dados.cidade) : null}`
                }
            }
            return `Imóveis para ${resposta[2] ? resposta[2] : 'venda'}${aux}`
        };
        const bannerListagem = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "listagem");
        const urlBannerListagem = bannerListagem ? bannerListagem.length > 0 ? bannerListagem[0] : null : null;
        const capitalizeFirstLetter = (text: string) => {
            if (!text) return text;
            return text.charAt(0).toUpperCase() + text.slice(1);
        };
        const fallbackTitle = renderOnlyTextFilters() ? capitalizeFirstLetter(renderOnlyTextFilters()) : site?.titulo;
        const seo = buildImoveisSeo({
            asPath,
            site,
            allDataHome,
            query: router.query,
            rotulos: props?.rotulosHifen,
        });
        // cs2586 (gate utils/seoContextoListagem): title/og:title iguais ao <title> da página (título da cidade
        // só na URL só da cidade; condomínio → meta_title/meta_description dele). Fora do gate: o de sempre.
        const seoContexto = temSeoContextoListagem(site);
        const headTitle = seoContexto
            ? tituloListagemPorContexto(asPath, {
                tituloCidade: props?.seoCidade?.titulo,
                tituloBusca: seo?.title || fallbackTitle,
                empreendimento: props?.seoEmpreendimentoListagem,
            }) || fallbackTitle
            : seo?.title || fallbackTitle;
        const headDescription =
            (seoContexto ? descricaoListagemPorContexto(asPath, props?.seoEmpreendimentoListagem) : '') ||
            seo?.description || site?.meta_description || fallbackTitle;
        // Canonical normalizado da busca: consolida as variantes de profundidade do path
        // (ex.: /imoveis/venda  ->  /imoveis/venda/-/-/-/-) numa ÚNICA URL de 6 segmentos,
        // idêntica à dos sitemaps, para o Google não tratar curta e completa como duplicatas.
        const buscaCanonicalUrl = (() => {
            const rawPath = String(asPath || '').split('?')[0].split('#')[0];
            const parts = rawPath.split('/').filter(Boolean);
            if (parts[0] !== 'imoveis') return canonicalUrl;
            const defaultModel = String(site?.transacoes?.[0] || 'venda').toLowerCase();
            const seg = [
                parts[1] || defaultModel,
                parts[2] || '-',
                parts[3] || '-',
                parts[4] || '-',
                parts[5] || '-',
                ...parts.slice(6),
            ];
            // INC-0278: filtros sem acento e em minúsculas — a MESMA regra do 301 do middleware
            const pathPorProfundidade = '/imoveis/' + seg.join('/');
            const normalizedPath = normalizarCaminhoListagem(pathPorProfundidade) ?? pathPorProfundidade;
            try {
                return `${new URL(canonicalUrl).origin}${normalizedPath}`;
            } catch {
                return normalizedPath;
            }
        })();
        return (
            <Head>
                <title>{headTitle}</title>
                <link rel="preconnect" href={"https://"+site?.site}/>
                <link rel="preconnect" href={"https://www."+site?.site}/>
                <link rel="preconnect" href="https://fotos.sobressai.com.br" crossOrigin="anonymous" />
                <link rel="dns-prefetch" href="https://fotos.sobressai.com.br" />
                <meta name="description" content={headDescription} />
                <meta name="twitter:title" content={headTitle} />
                <meta name="twitter:description" content={headDescription} />
                <meta property="og:url" content={buscaCanonicalUrl} />
                {/* soft-404: listagem de slug vazia (count=0 no SSR) sai do índice já no HTML.
                    O override client (naoEncontrou) cobre filtros in-session via a mesma key. */}
                <meta name="robots" content={props?.noindexEmpty ? "noindex, follow" : "index, follow"} key="robots" />
                <meta
                    property="og:title"
                    content={headTitle}
                ></meta>
                <meta
                    property="og:description"
                    content={headDescription}
                ></meta>
                <link rel="canonical" href={buscaCanonicalUrl} key="canonical" />
                <meta
                    property="og:image"
                    content={site?.id == 2204 ? site?.url_icon : site?.id == 2280 ? site?.url_logo : site?.id == 1616 ? "https://imob.sobressai.com.br/Banner/1616/11880.jpg" : urlBannerListagem ? urlBannerListagem?.url : "https://fotos.sobressai.com.br/Banner/1722/10526.jpg"}
                ></meta>
                <meta name="twitter:image" content={site?.id == 1616 ? site?.url_icon : urlBannerListagem ? urlBannerListagem?.url : "https://fotos.sobressai.com.br/Banner/1722/10526.jpg"} />
            </Head>
        )
    }

    /* --------------------- JSX ------------------------------------- */
    if (siteCongelado || (notFound && asPath !== '/404')) {
        return (
        <div style={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p>Redirecionando...</p>
        </div>
        );
    }

    const content = (
    <>
        <RenderHeadMarketingSnippets snippets={initialHeadMarketingSnippets} usePartytown={usePartytown} />
        <RenderBodyMarketingSnippets snippets={initialBodyMarketingSnippets} usePartytown={usePartytown} />
        <InjectMarketingSnippets snippets={preparedMarketingSnippets.valid} enabled={hasGoogleMarketingSnippetSource && marketingReady} usePartytown={usePartytown} />
        <TrackPageView enabled={canTrackGooglePageViews && marketingReady} />
        <TrackSite2434WhatsAppLead enabled={Number(site?.id) === 2434} />
        <UrlDaPaginaNoWhats enabled={siteAnexaUrlDaPaginaNoWhats(site, router.pathname)} />
        <UtmNoWhats />
        {siteUsaFormularioExternoNoWhats(site) ? <FormularioExternoNoWhats site={site} /> : null}
        {props?.imovel?.id || asPath.includes("/imovel/") ? RenderHeaderDetails() : asPath.includes("/imoveis") ? RenderImoveisDetails() : null}
        <RenderTenantCustomCss css={effectiveCustomCss} />
        <main
            className={`${robotoCondensed.className} ${roboto.className} ${syncopate.className} ${manrope.className}${usaNunitoSans(site) ? ` ${nunitoSans.className}` : ''}`}
            style={usaNunitoSans(site) ? ({ '--fonte-principal': NUNITO_SANS_FAMILY } as CSSProperties) : undefined}
        >
            <PreviaBuilderProvider value={previaBuilder}>
            <AuthProvider allDataHome={props?.allDataHome || allDataHome} init_props={props}>
                <GlobalThemeSetter />
                <Component {...props} />
                <FilialPopup />
                <FilialTelaPortao />
                {temSeletorIdiomas(site) ? <IdiomaOculto /> : null}
            </AuthProvider>
            </PreviaBuilderProvider>
        </main>
    </>
  );

  const content2 = (
    <>
        <RenderHeadMarketingSnippets snippets={initialHeadMarketingSnippets} usePartytown={usePartytown} />
        <RenderBodyMarketingSnippets snippets={initialBodyMarketingSnippets} usePartytown={usePartytown} />
        <InjectMarketingSnippets snippets={preparedMarketingSnippets.valid} enabled={hasGoogleMarketingSnippetSource && marketingReady} usePartytown={usePartytown} />
        <TrackPageView enabled={canTrackGooglePageViews && marketingReady} />
        <TrackSite2434WhatsAppLead enabled={Number(site?.id) === 2434} />
        <UrlDaPaginaNoWhats enabled={siteAnexaUrlDaPaginaNoWhats(site, router.pathname)} />
        <UtmNoWhats />
        {siteUsaFormularioExternoNoWhats(site) ? <FormularioExternoNoWhats site={site} /> : null}
        {props?.imovel?.id || asPath.includes("/imovel/") ? RenderHeaderDetails() : asPath.includes("/imoveis") ? RenderImoveisDetails() : null}
        <RenderTenantCustomCss css={effectiveCustomCss} />
        <main className={`${avenirBook.className}`}>
            <PreviaBuilderProvider value={previaBuilder}>
            <AuthProvider allDataHome={props?.allDataHome || allDataHome} init_props={props}>
                <GlobalThemeSetter />
                <Component {...props} />
                <FilialPopup />
                <FilialTelaPortao />
                {temSeletorIdiomas(site) ? <IdiomaOculto /> : null}
            </AuthProvider>
            </PreviaBuilderProvider>
        </main>
    </>
  );

  // se for o 2379, embrulha com o provider que aplica a Avenir
  return site?.id === 2379 ? content2 : content;
}

export default MyApp;





